/**
 * Diese Komponente bindet dann das jeweilige Modul ein.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { makeStyles } from '@mui/styles'
import { Box, Grid, Paper, Theme } from '@mui/material'

import MediaGalleryComponent
  from '@professocial/professocialFormComponents/FormComponents/MediaGallery/MediaGalleryComponent'
import { useSelector } from 'react-redux'
import {
  MediaFileMetadata,
  UploadMediaFileRequest
} from '@3m5/sm_publish-frontend-api'
import { getCoverImages, getMediaFile } from '../../../../store/images/images.selector'
import MediaUploadComponent from '@professocial/professocialFormComponents/FormComponents/MediaUploadComponent'
import LightBox from '@professocial/professocialFormComponents/FormComponents/LightBox'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { CoreRootState } from '../../../../store/coreRoot.reducer'
import { namedRequestInProgress, namedRequestStatus } from '../../../../store/common/common.selectors'
import {
  deleteMediaFileInitiated,
  setMediaFileUsageInitiated,
  uploadMediaFileInitiated,
} from '../../../../store/images/images.actions'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import VisibilityRounded from '@mui/icons-material/VisibilityRounded'

interface Props {
  getTitleImages: () => void
  deleteFile: (id: string) => void
  uploadNewFile: (request: UploadMediaFileRequest) => void
}

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    marginTop: '10px',
    padding: '25px 30px',
  },
  button: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body1.fontSize,
    textTransform: 'initial',
    marginRight: '20px',
  },
  upload: {
    margin: theme.spacing(2),
    marginLeft: 0,
  },
}))

const EditorImagesTitle: React.FC<Props> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const mediaData: MediaFileMetadata[] = useSelector(getCoverImages) || []
  const mediaFile: MediaFileMetadata | undefined = useSelector(getMediaFile)

  const loadingMediaFile: boolean = useSelector((state: CoreRootState) => namedRequestInProgress(state, uploadMediaFileInitiated.type))
  const loadingDeleteMediaFile: boolean = useSelector((state: CoreRootState) => namedRequestInProgress(state, deleteMediaFileInitiated.type))
  const loadingSetMediaFile: boolean = useSelector((state: CoreRootState) => namedRequestInProgress(state, setMediaFileUsageInitiated.type))
  const statusOnSetMediaFile: string | undefined = useSelector((state: CoreRootState) => namedRequestStatus(state, setMediaFileUsageInitiated.type))
  const statusOnDeleteMediaFile: string | undefined = useSelector((state: CoreRootState) => namedRequestStatus(state, deleteMediaFileInitiated.type))
  const statusOnUploadMediaFile: string | undefined = useSelector((state: CoreRootState) => namedRequestStatus(state, uploadMediaFileInitiated.type))

  const [uploadedMediaFile, setUploadedMediaFile] = useState<UploadMediaFileRequest>()
  const [selectedMedia, setSelectedMedia] = useState<MediaFileMetadata>()
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false)

  const handleSelectedMedia = (item: MediaFileMetadata, thumbnail: string | undefined) => {
    setSelectedMedia(item)
  }

  useEffect(() => {
    props.getTitleImages()
  }, [])

  useEffect(() => {
    if (statusOnUploadMediaFile && statusOnUploadMediaFile === 'success') {
      setUploadedMediaFile(undefined)
    }
    statusOnSetMediaFile && statusOnSetMediaFile === 'success' && props.getTitleImages()
    statusOnDeleteMediaFile && statusOnDeleteMediaFile === 'success' && props.getTitleImages()
  }, [statusOnSetMediaFile, statusOnDeleteMediaFile, statusOnUploadMediaFile])

  return (
    <>
      <Grid container>
        <Grid xs={12} item>
          <Paper className={classes.paper}>
            <Box>{t('coreImages:titleImagesDesc')}</Box><br/>
            <Box>{t('coreImages:titleImagesDesc2')}</Box>
            <Box>{t('coreImages:titleImagesDesc3')}</Box>
            <Box>{t('coreImages:titleImagesDesc4')}</Box><br/>
            <Box>{t('coreImages:titleImagesDesc5')}</Box>
            <Box className={classes.upload}>
              <MediaUploadComponent
                getMediaData={(params) => setUploadedMediaFile(params)}
                setMediaFile={(file) => {
                  if (file === undefined && uploadedMediaFile) {
                    setUploadedMediaFile(undefined)
                  }
                }}
                loading={loadingMediaFile}
                isUploaded={!mediaFile}
                minCropBoxHeight={315}
                minCropBoxWidth={851}
                containerHeight={315}
                containerWidth={851}
                cropBoxResizable={false}
                allowVideoUpload={false}
                allowImageUpload
                onlyUploadButton
                viewMode={3}
              />
              {uploadedMediaFile && <Box sx={{ pt: 3 }}>
                <PreloaderButton
                  loading={loadingMediaFile}
                  disableButton={loadingMediaFile}
                  position='left'
                  onClick={() => props.uploadNewFile(uploadedMediaFile)}
                  text={t('commonImage:uploadNewFile')}
                />
              </Box>}
            </Box>
          </Paper>
        </Grid>
        <Grid xs={12} item>
          <Paper className={classes.paper}>
            <MediaGalleryComponent
              mediaData={mediaData}
              setSelected={handleSelectedMedia}
              selected={selectedMedia}
              wideView
            />
          </Paper>
        </Grid>

        <Box component='div' style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', marginTop: '10px' }}>
          <LightBox
            src={selectedMedia?.directLink}
            disabled={!selectedMedia || Object.keys(selectedMedia).length === 0}
          >
            <ButtonComponent
              disabled={!selectedMedia || Object.keys(selectedMedia).length === 0}
              className={classes.button}
              startIcon={<VisibilityRounded />}
              color='info'
              variant='text'
              text={t('postingCommon:preview')}
            />
          </LightBox>
          <PreloaderButton
            position='right'
            loading={loadingDeleteMediaFile || loadingSetMediaFile}
            disableButton={!selectedMedia || Object.keys(selectedMedia).length === 0 || loadingDeleteMediaFile || loadingSetMediaFile}
            text={t('coreCommon:delete')}
            onClick={() => setDeleteDialog(true)}
          />
          <DialogComponent
            open={deleteDialog}
            type='warning'
            title={t('coreImages:deleteFileWarning')}
            requestActions
            confirmLabel={t('coreImages:deleteFile')}
            cancelLabel={t('coreCommon:backToPAge')}
            autoClose={false}
            closeIcon={false}
            onClose={() => setDeleteDialog(false)}
            onConfirm={() => {
              selectedMedia && props.deleteFile(selectedMedia.mediaFileId)
              setDeleteDialog(false)
              props.getTitleImages()
            }}
            onCancel={() => setDeleteDialog(false)}
          />
        </Box>
      </Grid>
    </>
  )
}
export default EditorImagesTitle
